import { legalService, TLegalAcknowledgement, TLegalDocumentType } from "@/api/legal";
import { DOCUMENT_TYPE_LABELS } from "@/components/admin/legal";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TFilterDef,
} from "@/components/data-grid";
import { AdminPageLayout } from "@/components/layout/AdminPageLayout";

const PAGE_SIZE = 20;

const DOCUMENT_TYPE_FILTER_OPTIONS = [
  { label: "Terms", value: "terms_of_service" },
  { label: "Privacy", value: "privacy_policy" },
  { label: "Cookies", value: "cookies_policy" },
];

export function LegalAcknowledgementsPage() {
  // Server mode, page-based. The endpoint has no search/sort params — only
  // user_id / document_type filters — so no search/sortable controls are wired.
  const fetcher = async (query: TDataGridQuery) => {
    const userId = query.filters.user_id;
    const documentType = query.filters.document_type;
    const { error, data } = await legalService.listAcknowledgements(
      {
        page: query.page,
        page_size: query.per_page,
        ...(typeof userId === "string" && userId ? { user_id: userId } : {}),
        ...(typeof documentType === "string" && documentType
          ? { document_type: documentType as TLegalDocumentType }
          : {}),
      },
      { signal: query.signal }
    );
    if (error) throw error;
    return data;
  };

  const columns: TColumnDef<TLegalAcknowledgement>[] = [
    {
      id: "user_id",
      header: "User ID",
      cell: (item) => (
        <span className="max-w-xs truncate font-mono text-xs text-foreground" title={item.user_id}>
          {item.user_id}
        </span>
      ),
    },
    {
      id: "document_type",
      header: "Document type",
      accessor: (item) => DOCUMENT_TYPE_LABELS[item.document_type] ?? item.document_type,
    },
    {
      id: "document_version",
      header: "Version",
      accessor: (item) => `v${item.document_version}`,
    },
    { id: "accepted_at", header: "Accepted at", type: "date", accessor: (item) => item.accepted_at },
  ];

  const filters: TFilterDef[] = [
    { id: "user_id", type: "user", label: "User", placeholder: "Search by name or email…" },
    {
      id: "document_type",
      type: "select",
      label: "Document type",
      options: DOCUMENT_TYPE_FILTER_OPTIONS,
    },
  ];

  return (
    <AdminPageLayout
      title="Acknowledgement Logs"
      subtitle="Audit trail of user legal acceptances. Read-only compliance view."
    >
      <DataGrid<TLegalAcknowledgement>
        getRowId={(row) => row.id}
        persistKey="admin.legal-acknowledgements"
        ariaLabel="Legal acknowledgements"
        data={{ mode: "server", fetcher }}
        columns={columns}
        filters={filters}
        pagination={{ mode: "page", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
        toolbar={{
          refresh: true,
          columnVisibility: true,
          density: true,
          export: ["csv", "json"],
        }}
        emptyState={{
          title: "No acknowledgement logs yet",
          description: "Logs appear when users accept legal documents.",
        }}
      />
    </AdminPageLayout>
  );
}
